import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { UsageCharts, type StatsRow } from '@/components/Charts';
import { PageHeader } from '@/components/Layout';
import { TraceList } from '@/components/TraceList';
import { Button, Stat } from '@/components/ui';
import { api } from '@/lib/api';
import { fmtInt, fmtMs, fmtUsd } from '@/lib/format';
import { rk } from '@/lib/hooks';

const RANGES = [
  { hours: 24, label: '24h' },
  { hours: 24 * 7, label: '7d' },
  { hours: 24 * 30, label: '30d' },
];

export const Requests = () => {
  const [hours, setHours] = useState(24);
  const stats = useQuery({ queryKey: rk('stats', 'all', hours), queryFn: () => api<{ bucket: 'hour' | 'day'; series: StatsRow[] }>(`/v1/stats?hours=${hours}`) });
  const s = stats.data?.series ?? [];
  const sum = (k: 'requests' | 'errors' | 'inputTokens' | 'outputTokens') => s.reduce((a, r) => a + r[k], 0);
  const costs = s.map((r) => r.costUsd).filter((c): c is number => c !== null);
  return (
    <>
      <PageHeader
        title="Requests"
        subtitle="Every agent run, with prompts, tool calls, tokens, latency and cost. Production traffic only in the charts; kept for 90 days."
        actions={
          <div className="flex gap-1">
            {RANGES.map((r) => (
              <Button key={r.hours} size="sm" variant={r.hours === hours ? 'primary' : 'ghost'} onClick={() => setHours(r.hours)}>
                {r.label}
              </Button>
            ))}
          </div>
        }
      />
      <div className="mb-4 grid grid-cols-2 gap-5 rounded-lg border border-line bg-panel p-4 sm:grid-cols-3 lg:grid-cols-5">
        <Stat label="Requests" value={fmtInt(sum('requests'))} />
        <Stat label="Failed" value={fmtInt(sum('errors'))} tone={sum('errors') ? 'bad' : undefined} />
        <Stat label="Worst p95" value={fmtMs(s.length ? Math.max(...s.map((r) => r.p95Ms ?? 0)) : null)} />
        <Stat label="Tokens in / out" value={`${fmtInt(sum('inputTokens'))} / ${fmtInt(sum('outputTokens'))}`} />
        <Stat label="Cost" value={costs.length ? fmtUsd(costs.reduce((a, b) => a + b, 0)) : '—'} />
      </div>
      {stats.data && <UsageCharts className="mb-5" rows={stats.data.series} hours={hours} bucket={stats.data.bucket} />}
      <TraceList />
    </>
  );
};
